import React, { ReactNode, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { useFactory } from '@funtech-int/rival-react';
import { PlayerWatchParams } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { useFeatureFlags } from '@funtech-int/yango-shared';
import { PlayerData } from '@funtech-int/kp-graphql-sdk/player-sdk';

import { createSDKPlayerBuilder } from '@kinopoisk-int/filmoscope';

import { useGraphqlClient } from '@/shared/hooks/useGraphqlClient';
import { useProfileManager } from '@/shared/hooks/useProfileManager';
import { clientConfig } from '@/config/client';

import { createFilmoscopeRequester } from '../../lib/createFilmoscopeRequester';
import { PlaybackContext } from '../../lib/playbackContext';
import { createEnhancedPlayback, EnhancedPlayback } from '../../lib/enhancedPlayback';
import { formatTestIds } from '../../lib/formatTestIds';
import { PlaybackMetrics, usePlaybackMetrics } from '../../hooks/usePlaybackMetrics';

import styles from './PlaybackProvider.module.css';

/**
 * Allowed values for the player's `from_block` parameter.
 * They describe where in the app playback can start from,
 * so analytics can tell which part of the app initiated playback.
 */
type FromBlock =
    /** Series playback. Generic player value. */
    | 'player-screen'
    /** Film playback. Generic player value. */
    | 'player-screen'
    /** Movie playback. Generic player value. */
    | 'movie-screen'
    /** Trailer playback. */
    | 'trailer-screen'
    /** Channel playback. */
    | 'tv-player-screen'
    /** Sports channel playback. */
    | 'sport-player-screen'
    /** Playback from a promo block or curated video shelf on the storefront. */
    | 'main-screen'
    /** Playback from a sports promo block on the storefront. */
    | 'sport-main-screen'
    /** Trailer playback on a title card. */
    | 'movie-screen';

type StreamBlock = 'trailer' | 'channel' | 'sport-live' | 'movie' | 'episode' | 'clip';

interface PlaybackProviderProps {
    contentId: string;
    fromBlock: FromBlock;
    streamBlock: StreamBlock;
    children: ReactNode;
    loader: ReactNode;
    startPosition?: number;
    objectFit?: 'contain' | 'cover';
    muted?: boolean;
    watchParams?: PlayerWatchParams | null;
    autoPlay?: boolean;
    metrics: PlaybackMetrics;
}

export const PlaybackProvider: React.FC<PlaybackProviderProps> = ({
    contentId,
    fromBlock,
    streamBlock,
    children,
    watchParams,
    loader,
    startPosition,
    objectFit = 'contain',
    muted = false,
    autoPlay = false,
    metrics,
}) => {
    const [loaded, setLoaded] = useState<{ playback: EnhancedPlayback; contentId: string } | null>(
        null
    );
    const [error, setError] = useState<Error | null>(null);

    if (error) {
        throw error;
    }

    const containerRef = useRef<HTMLDivElement>(null);
    const playerDataRef = useRef<PlayerData | undefined>(undefined);
    const graphqlClient = useGraphqlClient();
    const { userProfile } = useProfileManager();
    const { flags, testIds } = useFeatureFlags();
    const isPlayerDebugMode = flags.get('isPlayerDebugMode', 'boolean', false);
    const filmoscopeVersion = flags.get('filmoscopeVersion', 'string', '');

    const player = useFactory(() => {
        const onData = (data: PlayerData) => {
            playerDataRef.current = data;
        };

        const requester = createFilmoscopeRequester(graphqlClient, onData);

        return createSDKPlayerBuilder()
            .registerContentConfig({ requester })
            .debug(isPlayerDebugMode)

            .telemetry({
                logUrl: clientConfig.playerTelemetryUrl,
                perfLogUrl: clientConfig.playerPerfUrl,
                from: clientConfig.productServiceName,
            })
            .testIds(formatTestIds(testIds))

            .playback({
                restrictABRToElementSize: true,
            })
            .yasp({
                scriptUrl: clientConfig.filmoscopeYaspScriptUrl,
                disableYaspLoader: Boolean(clientConfig.filmoscopeYaspScriptUrl),
            })
            .load({
                baseStaticPath: clientConfig.filmoscopeBaseStaticPath,
            })
            .useEngineName('yasp')
            .version(filmoscopeVersion)
            .build();
    });

    useEffect(
        () => () => {
            player.destroy();
        },
        [player]
    );

    useEffect(() => {
        const abortController = new AbortController();

        player
            .createPlayback(
                {
                    contentId,
                    from: clientConfig.productServiceName,
                    fromBlock,
                    streamBlock,
                    puid: userProfile?.puid?.toString() ?? '',
                    startPosition,
                },
                {
                    container: containerRef.current ?? undefined,
                    signal: abortController.signal,
                }
            )
            .then((sdkPlayback) => {
                if (!abortController.signal.aborted) {
                    const playback = createEnhancedPlayback(sdkPlayback, {
                        muted,
                        audioLanguage: watchParams?.audioLanguage,
                        subtitleLanguage: watchParams?.subtitleLanguage,
                        get playerData() {
                            return playerDataRef.current;
                        },
                    });

                    setLoaded({
                        playback,
                        contentId,
                    });

                    if (isPlayerDebugMode && typeof window !== 'undefined') {
                        window.playerInstance = sdkPlayback;
                    }
                }
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setError(new Error('Playback creation failed'));
                }
            });

        return () => {
            abortController.abort();
        };
    }, [
        contentId,
        fromBlock,
        isPlayerDebugMode,
        muted,
        player,
        startPosition,
        streamBlock,
        userProfile?.puid,
        watchParams?.audioLanguage,
        watchParams?.subtitleLanguage,
    ]);

    const isCurrent = loaded?.contentId === contentId;
    const playbackValue = isCurrent ? loaded.playback : null;

    usePlaybackMetrics(playbackValue, metrics);

    useEffect(() => {
        if (!playbackValue) {
            return;
        }

        if (autoPlay) {
            playbackValue.play();
        }

        return () => {
            playbackValue.destroy();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playbackValue]);

    return (
        <PlaybackContext.Provider value={playbackValue}>
            {isCurrent ? children : <div className={styles.loaderContainer}>{loader}</div>}
            <div
                ref={containerRef}
                className={classnames(styles.playbackContainer, {
                    [styles.containVideo]: objectFit === 'contain',
                    [styles.coverVideo]: objectFit === 'cover',
                })}
            />
        </PlaybackContext.Provider>
    );
};
